import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { BackButton, Loader } from '../../../components';
import ActionMenu from '../../../components/action-buttons/ActionButtons';
import AnimalBanner from '../../../components/animal/AnimalBanner';
import PagedTable from '../../../components/sorted-table/PagedTable';
import QuickView from '../../../components/quick-view/QuickView';
import SortedTable from '../../../components/sorted-table/SortedTable';
import SpecimenLotInfo from '../../../components/specimen-lot-info/SpecimenLotInfo';
import AnimalManagementHeader from '../AnimalManagementHeader';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import { ANIMAL_MANAGEMENT_PATHS, ROUTE_PATHS } from '../../../constants/routePaths';
import { PromiseError, toastMessages } from '../../../constants/errorMessages';
import { showToast } from '../../../services/toast.service';
import { Account } from '../../../types/interfaces/account.interfaces';
import { Animal, AnimalOwner } from '../../../types/interfaces/animal.interfaces';
import { PagedResponse, StandardParams } from '../../../types/interfaces/apiParams.interfaces';
import { Specimen } from '../../../types/interfaces/specimen.interfaces';
import { getAccounts } from '../../../api/accountApi';
import { getAnimalOwnership } from '../../../api/animalOwnersApi';
import { updateAnimalExportReady } from '../../../api/animalsApi';
import { getCountBySpecimen } from '../../../api/inventoryApi';
import { getSpecimens } from '../../../api/specimensApi';
import ToggleSwitch from '../../../components/custom-input/ToggleSwitch';
import AnimalDocuments from './AnimalDocuments';
import './animalOverview.scss';

interface HistoryItem extends Specimen {
  currentQuantity: number;
}

const AnimalOverview = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();

  const [animalInventoryHistory, setAnimalInventoryHistory] = useState<HistoryItem[]>();
  const [history, setHistory] = useState<HistoryItem[]>();
  const [isExportReady, setIsExportReady] = useState<boolean>(false);
  const [isExportReadyUpdating, setIsExportReadyUpdating] = useState<boolean>(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState<boolean>(false);
  const [loadingOwnership, setLoadingOwnership] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [ownership, setOwnership] = useState<AnimalOwner[]>();
  const [specimen, setSpecimen] = useState<Specimen>();

  const handleAnimalLoad = useCallback((animal: Animal) => {
    setIsExportReady(!!animal.isExportReady);
  }, []);

  const handleExportReadyChange = async (checked: boolean) => {
    if (!animalId || isExportReadyUpdating) return;

    const previousValue = isExportReady;
    setIsExportReady(checked);
    setIsExportReadyUpdating(true);

    try {
      await updateAnimalExportReady(animalId, checked);
      showToast.success(toastMessages.ANIMAL_EXPORT_READY_UPDATED);
    } catch (error: any) {
      setIsExportReady(previousValue);
      if (error.code === PromiseError.NETWORK) {
        showToast.error(toastMessages.NETWORK);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } finally {
      setIsExportReadyUpdating(false);
    }
  };

  const handleOwnership = useCallback(async () => {
    if (animalId) {
      setLoadingOwnership(true);
      try {
        const query = { include: 'Animal,Account', filter: `AnimalId eq ${animalId}` };
        const { data: animalOwnership } = await getAnimalOwnership(query);

        if (Array.isArray(animalOwnership)) {
          const accountIds: number[] = Array.from(new Set(animalOwnership.map((o: AnimalOwner) => o.accountId!)));

          if (animalOwnership.length > 0 && animalOwnership[0].animal?.name) {
            setName(animalOwnership[0].animal?.name);
          }

          await handleOwnerAccount(accountIds, animalOwnership);
        } else {
          throw new Error('Received unexpected type');
        }
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
      setLoadingOwnership(false);
    }
  }, [animalId]);

  const handleHistory = useCallback(
    async (params?: StandardParams) => {
      const res = await getHistory(params);
      setHistory(res?.data);
    },
    [animalId],
  );

  const getHistory = async (params?: StandardParams) => {
    if (animalId) {
      try {
        const query = { filter: params?.filter ?? `AnimalId eq ${animalId}`, include: params?.include, sort: params?.sort };
        const { data: specimens } = await getSpecimens(query);

        const quantityPromises = specimens.map(async specimenItem => {
          const { data: currentQuantity } = await getCountBySpecimen(specimenItem.specimenId);
          return { ...specimenItem, currentQuantity };
        });

        setAnimalInventoryHistory(await Promise.all(quantityPromises));
        return { data: await Promise.all(quantityPromises) };
      } catch (error: any) {
        if (error.code === PromiseError.NETWORK) {
          showToast.error(toastMessages.NETWORK);
        } else {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    }
  };

  useEffect(() => {
    handleHistory();
  }, [handleHistory]);

  useEffect(() => {
    handleOwnership();
  }, [handleOwnership]);

  const handleOwnerAccount = async (ids: number[], owners: AnimalOwner[]) => {
    if (ids.length && owners.length) {
      try {
        const { data: accounts } = await getAccounts(ids);
        const enrichedOwners = owners.map(owner => ({ ...owner, account: getAccountById(accounts, owner) }));
        setOwnership(enrichedOwners);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } else {
      setOwnership([]);
    }
  };

  const getAccountById = (accounts: Account[] | PagedResponse<Account>, owner: AnimalOwner): Account | undefined => {
    if (Array.isArray(accounts)) {
      return accounts.find((a: Account) => a.accountId === owner.accountId);
    }
  };

  const handleQuickViewOpen = (item: HistoryItem) => {
    if (!item || !item.specimenId) throw Error('Cannot open QuickView Modal because inventory was undefined.');

    setSpecimen(item);
    setIsQuickViewOpen(true);
  };

  const actionMenuButton = (item: HistoryItem) => {
    return (
      <ActionMenu
        actionButtons={[
          {
            name: BUTTON_CONSTANTS.VIEW_DETAILS,
            action: () => navigate(ROUTE_PATHS.APP_ANIMAL_MANAGEMENT_ + animalId + '/' + item.specimenId),
          },

          {
            name: BUTTON_CONSTANTS.QUICK_VIEW,
            action: () => handleQuickViewOpen(item),
          },
        ]}
      />
    );
  };

  const getMobileInventoryTableView = () => {
    return (
      animalInventoryHistory &&
      animalInventoryHistory.map((item, index) => {
        return (
          <div className="card max-width" key={'animal-' + item?.specimenId + index}>
            <div className="animal-inventory-card">
              <div className="animal-inventory-content">
                <div className="card-content-section">
                  <div className="inventory-content">
                    <label>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}:</label>
                    <label>{`${item.specimenType?.name ?? '-'}`}</label>
                  </div>
                  <div className="inventory-content">
                    <label>{TABLE_HEADER_CONSTANTS.LOT_DATE_NO}:</label>
                    <label>
                      <SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} freezeDate={item.freezeDate} />
                    </label>
                  </div>
                  <div className="inventory-content">
                    <label>{TABLE_HEADER_CONSTANTS.QUALITY}:</label>
                    <label>{`${item.quality ?? '-'}`}</label>
                  </div>
                </div>
                <div className="card-content-section">
                  <div className="inventory-content">
                    <label>{TABLE_HEADER_CONSTANTS.QUALITY_PERCENTAGE}:</label>
                    <label>{`${item.qualityPercentage ?? '-'}`}</label>
                  </div>
                  <div className="inventory-content">
                    <label>{TABLE_HEADER_CONSTANTS.ORIGINAL_QTY}:</label>
                    <label>{`${item.originalQuantity ?? '-'}`}</label>
                  </div>
                  <div className="inventory-content">
                    <label>{TABLE_HEADER_CONSTANTS.CURRENT_QTY}:</label>
                    <label>{`${item.currentQuantity ?? '-'}`}</label>
                  </div>
                </div>
              </div>
              <div className="action-button">{actionMenuButton(item)}</div>
            </div>
          </div>
        );
      })
    );
  };

  return (
    <div className="animal-overview">
      {specimen && (
        <QuickView
          specimen={specimen}
          isOpen={isQuickViewOpen}
          onClose={() => setIsQuickViewOpen(false)}
          setIsOpen={setIsQuickViewOpen}
        />
      )}

      <AnimalManagementHeader />
      <BackButton />
      <AnimalBanner animalId={animalId} onAnimalLoad={handleAnimalLoad}>
        <div className="animal-actions-bar">
          <ToggleSwitch
            label={ANIMAL_CONSTANTS.EXPORT_READY}
            checked={isExportReady}
            disabled={isExportReadyUpdating}
            onToggle={() => handleExportReadyChange(!isExportReady)}
          />
          <button
            className="button blue small"
            onClick={() => navigate(ROUTE_PATHS.APP_ANIMAL_MANAGEMENT_ + animalId + '/' + ANIMAL_MANAGEMENT_PATHS.EDIT)}>
            {BUTTON_CONSTANTS.EDIT}
          </button>
        </div>
      </AnimalBanner>

      <div className="animal-overview-ownership">
        <h2>{name || `Animal's`} Inventory</h2>
        {history === undefined ? (
          <Loader addedSpace loaderSize="small" />
        ) : history?.length > 0 ? (
          <>
            <div className="desk-animal-overview-ownership">
              <PagedTable
                headers={[
                  TABLE_HEADER_CONSTANTS.INVENTORY_TYPE,
                  TABLE_HEADER_CONSTANTS.LOT_DATE_NO,
                  TABLE_HEADER_CONSTANTS.QUALITY,
                  TABLE_HEADER_CONSTANTS.QUALITY_PERCENTAGE,
                  TABLE_HEADER_CONSTANTS.ORIGINAL_QUANTITY,
                  TABLE_HEADER_CONSTANTS.CURRENT_QTY,
                  TABLE_HEADER_CONSTANTS.ACTION_ITEM,
                ].map(header => {
                  return { displayName: header };
                })}
                filter={'AnimalId eq ' + animalId}
                include="SpecimenType,SpecimenCustomDataValues.SpecimenCustomDataKey,EmbryoStraws"
                sortBy="^freezeDate"
                getData={getHistory}
                buildRow={(item: HistoryItem) => {
                  return [
                    item.specimenType?.name,
                    <SpecimenLotInfo specimenId={item.specimenId} lotNumber={item?.lotNumber} freezeDate={item.freezeDate} />,
                    item.quality,
                    item.qualityPercentage,
                    item.originalQuantity,
                    item.currentQuantity,
                    actionMenuButton(item),
                  ];
                }}
                pageSize={0}
                height={300}
                minHeight={130}
              />
            </div>

            {/** Mobile View */}
            <div className="xs-animal-overview-ownership">{getMobileInventoryTableView()}</div>
          </>
        ) : (
          <h4 className="no-ownership">{LABEL_CONSTANTS.NO_INVENTORY}</h4>
        )}

        <h2>{ANIMAL_CONSTANTS.ANIMAL_OWNERSHIP_BREAKDOWN}</h2>
        <div className="animal-ownership-table">
          <SortedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.ACCOUNT_BUSINESS_FARM },
              { displayName: TABLE_HEADER_CONSTANTS.EMAIL_ADDRESS },
              { displayName: TABLE_HEADER_CONSTANTS.OWNERSHIP_PERCENTAGE },
            ]}
            data={ownership?.map((o: any) => {
              return [{ content: o.account.name }, { content: o.account.contactEmail }, { content: o.ownershipPercentage }];
            })}
            loading={loadingOwnership}
            noResultsMessage={ANIMAL_CONSTANTS.NO_OWNERSHIP}
          />
        </div>

        <AnimalDocuments animalId={animalId} />
      </div>
    </div>
  );
};

export default AnimalOverview;
